import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import {
  DEFAULT_BERICHT_EINSTELLUNGEN,
  type BerichtEinstellungen,
  type GenericResponse,
  type Schwere,
  type TeamsAlert,
  type TeamsAlertsConfig,
} from "@/lib/types";

const WOCHENTAGE: { wert: number; label: string }[] = [
  { wert: 1, label: t("Montag") },
  { wert: 2, label: t("Dienstag") },
  { wert: 3, label: t("Mittwoch") },
  { wert: 4, label: t("Donnerstag") },
  { wert: 5, label: t("Freitag") },
  { wert: 6, label: t("Samstag") },
  { wert: 7, label: t("Sonntag") },
];

const feld =
  "h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary";
const titel = "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

/** Liste und Textfeld: der Nutzer tippt „06:00, 14:00", gespeichert wird ein Feld. */
function alsText(werte: string[]): string {
  return werte.join(", ");
}

function ausText(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function kanalArt(k: TeamsAlert): string {
  if (k.channel_type === "email") return t("E-Mail");
  if (k.channel_type === "webhook") return "Webhook";
  if (k.channel_type === "nextcloud_talk") return "Nextcloud Talk";
  return "Microsoft Teams";
}

/** Warum ein Kanal den Bericht nicht tragen kann. Leer = brauchbar. */
function kanalMangel(k: TeamsAlert): string {
  if (!k.enabled) return t("abgeschaltet");
  if (k.channel_type === "email") return k.email_to.trim() ? "" : t("ohne Empfänger");
  if (k.channel_type === "nextcloud_talk" && !(k.talk_secret ?? "").trim()) return t("ohne Geheimnis");
  return k.webhook_url.trim() ? "" : t("ohne Adresse");
}

export function BerichtPage() {
  const [cfg, setCfg] = useState<BerichtEinstellungen>(DEFAULT_BERICHT_EINSTELLUNGEN);
  const [zeitenText, setZeitenText] = useState("07:00");
  const [clientsText, setClientsText] = useState("");
  const [kanaele, setKanaele] = useState<TeamsAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, alarme] = await Promise.all([
        api.get<BerichtEinstellungen>("/api/settings/bericht"),
        api.get<TeamsAlertsConfig>("/api/settings/teams-alerts"),
      ]);
      setCfg(c);
      setKanaele(alarme.alerts ?? []);
      setZeitenText(alsText(c.taeglich_um));
      setClientsText(alsText(c.clients));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    const body: BerichtEinstellungen = {
      ...cfg,
      taeglich_um: ausText(zeitenText),
      clients: ausText(clientsText),
    };
    try {
      const resp = await api.post<GenericResponse>("/api/settings/bericht", body);
      setFeedback({ kind: resp.success ? "success" : "error", text: resp.message });
      if (resp.success) await reload();
    } catch (err) {
      setFeedback({
        kind: "error",
        text: err instanceof Error ? err.message : t("Speichern fehlgeschlagen"),
      });
    } finally {
      setSaving(false);
    }
  };

  const waehle = (name: string, an: boolean) => {
    setCfg((cur) => ({
      ...cur,
      kanaele: an
        ? [...cur.kanaele.filter((k) => k !== name), name]
        : cur.kanaele.filter((k) => k !== name),
    }));
  };
  const verwaist = cfg.kanaele.filter((name) => !kanaele.some((k) => k.name === name));

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{t("Lade…")}</div>;
  }

  return (
    <form onSubmit={onSave} className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("Zeitplan")}
        description={t("Mehrere Uhrzeiten ergeben Schichtberichte: Der Bericht um 14:00 deckt dann den Zeitraum seit 06:00 ab.")}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="h-4 w-4"
            />
            <span className="text-sm">{t("Berichte automatisch erzeugen und versenden")}</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className={titel}>{t("Tägliche Uhrzeiten")}</span>
            <input
              type="text"
              value={zeitenText}
              onChange={(e) => setZeitenText(e.target.value)}
              placeholder="06:00, 14:00, 22:00"
              className={feld + " font-mono text-xs"}
            />
            <span className="text-[11px] text-muted-foreground">
              {t("Kommagetrennt, Ortszeit des Servers. Leer = kein Tagesbericht.")}
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className={titel}>{t("Wochenbericht am")}</span>
              <select
                value={cfg.woechentlich_am ?? 0}
                onChange={(e) =>
                  setCfg({ ...cfg, woechentlich_am: Number(e.target.value) || null })
                }
                className={feld + " cursor-pointer"}
              >
                <option value={0}>{t("aus")}</option>
                {WOCHENTAGE.map((w) => (
                  <option key={w.wert} value={w.wert}>
                    {w.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className={titel}>{t("um")}</span>
              <input
                type="text"
                value={cfg.woechentlich_um}
                onChange={(e) => setCfg({ ...cfg, woechentlich_um: e.target.value })}
                placeholder="07:30"
                className={feld + " font-mono text-xs"}
              />
            </label>
          </div>
        </div>
      </Card>

      <Card
        title={t("Was als auffällig gilt")}
        description={t("Verglichen wird jede Anlage mit sich selbst: der Zeitraum gegen ihren eigenen Schnitt der letzten Tage.")}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className={titel}>{t("Vergleichszeitraum (Tage)")}</span>
            <input
              type="number"
              min={1}
              max={90}
              value={cfg.grundlinie_tage}
              onChange={(e) => setCfg({ ...cfg, grundlinie_tage: Number(e.target.value) })}
              className={feld + " font-mono text-xs"}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className={titel}>{t("Ab dieser Schwere berichten")}</span>
            <select
              value={cfg.min_schwere}
              onChange={(e) => setCfg({ ...cfg, min_schwere: e.target.value as Schwere })}
              className={feld + " cursor-pointer"}
            >
              <option value="hinweis">{t("Hinweis")}</option>
              <option value="warnung">{t("Warnung")}</option>
              <option value="dringend">{t("Dringend")}</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 md:col-span-2">
            <span className={titel}>{t("Nur diese Anlagen")}</span>
            <input
              type="text"
              value={clientsText}
              onChange={(e) => setClientsText(e.target.value)}
              placeholder={t("leer = alle")}
              className={feld + " font-mono text-xs"}
            />
          </label>

          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={cfg.nur_bei_befund}
              onChange={(e) => setCfg({ ...cfg, nur_bei_befund: e.target.checked })}
              className="h-4 w-4"
            />
            <span className="text-sm">{t("Nur senden, wenn es etwas Neues gibt")}</span>
          </label>
          <p className="text-[11px] text-muted-foreground md:col-span-2">
            {t("Empfohlen. Ein täglicher Bericht, der immer gleich aussieht, wird nach zwei Wochen nicht mehr geöffnet, und dann fällt auch der eine auf, der anders aussieht, niemandem mehr auf.")}
          </p>
        </div>
      </Card>

      <Card
        title={t("Versand")}
        description={t("Der Bericht geht über die Kanäle, die unter Alarmierung eingerichtet sind. Adressen und Empfänger werden nur dort gepflegt.")}
      >
        <div className="flex flex-col gap-3">
          <p className="text-[11px] text-muted-foreground">
            {t("Der Bericht geht als PDF mit Diagrammen raus: in der E-Mail als Anhang, in Teams und Nextcloud Talk als Link (beide nehmen keine Dateien an), im Webhook als Adresse und Inhalt. Der Link gilt 90 Tage und braucht keine Anmeldung.")}
          </p>
          {kanaele.length === 0 && verwaist.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("Noch kein Kanal eingerichtet.")}{" "}
              <Link to="/alarmierung" className="text-primary underline-offset-2 hover:underline">
                {t("Zur Alarmierung")}
              </Link>
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {kanaele.map((k) => {
                const mangel = kanalMangel(k);
                return (
                  <li key={k.name}>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={cfg.kanaele.includes(k.name)}
                        onChange={(e) => waehle(k.name, e.target.checked)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">{k.name || t("(unbenannt)")}</span>
                      <span className="text-[11px] text-muted-foreground">{kanalArt(k)}</span>
                      {mangel ? (
                        <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium text-warning">
                          {mangel}
                        </span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
              {/* Gewählt, aber unter Alarmierung inzwischen gelöscht oder
                  umbenannt: sichtbar lassen, damit man ihn abwählen kann. */}
              {verwaist.map((name) => (
                <li key={name}>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => waehle(name, false)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm line-through">{name}</span>
                    <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-medium text-destructive">
                      {t("gibt es nicht mehr")}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-muted-foreground">
            {t("Ein Kanal ohne Muster löst nie einen Alarm aus. Wer den Bericht will, aber keine Alarme, legt genau so einen an.")}
          </p>

          <label className="flex flex-col gap-1">
            <span className={titel}>{t("Adresse dieser Leif-Installation")}</span>
            <input
              type="text"
              value={cfg.basis_url}
              onChange={(e) => setCfg({ ...cfg, basis_url: e.target.value })}
              placeholder="https://leif.werk.example"
              className={feld + " max-w-lg font-mono text-xs"}
            />
            <span className="text-[11px] text-muted-foreground">
              {t("Für die Links im Bericht, den Link zum PDF und den Link „In Leif öffnen“ in Alarmen. Leer = keine Links.")}
            </span>
          </label>
        </div>
      </Card>

      <Card
        title={t("Einordnung durch das Sprachmodell")}
        description={t("Optional. Zwei bis vier Sätze, die sagen, worauf zuerst zu schauen ist.")}
      >
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cfg.ki_text}
            onChange={(e) => setCfg({ ...cfg, ki_text: e.target.checked })}
            className="h-4 w-4"
          />
          <span className="text-sm">{t("Einordnung anfordern")}</span>
        </label>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t("Läuft über den Lizenzserver und wird vom Guthaben dieser Installation bezahlt. Das Modell bekommt nur die fertig gerechneten Befunde, keine Rohdaten, und rechnet nicht selbst. Ist es nicht erreichbar, fehlt genau dieser Absatz und sonst nichts.")}
        </p>
      </Card>

      <Card
        title={t("Fragen an die Daten")}
        description={t("Ein Gespräch auf der Seite Berichte: Wer angemeldet ist, kann in eigenen Worten fragen, etwa wann an einer Anlage eine Meldung besonders häufig kam.")}
      >
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cfg.fragen}
            onChange={(e) => setCfg({ ...cfg, fragen: e.target.checked })}
            className="h-4 w-4"
          />
          <span className="text-sm">{t("Fragen an die Daten erlauben")}</span>
        </label>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t("Läuft über den Lizenzserver und wird vom Guthaben dieser Installation bezahlt. Anders als bei der Einordnung sieht das Modell hier gezählte Werte und einzelne Meldungen im Wortlaut, die Leif für die Frage abruft. Leif zählt selbst und zeigt die Zahlen unter jeder Antwort; Zahlen in der Antwort, die dort nicht stehen, werden markiert.")}
        </p>
      </Card>

      <div>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
        </button>
      </div>
    </form>
  );
}
